import React, { useState } from 'react'
import { Field, useForm } from 'react-final-form'
import { gsColors } from '../../../styles/Variables'
import InputField from '../input-field'
import styled from '@emotion/styled'
import { Text } from '../../../styles/Components'
import TextAreaField from '../textarea-field'
import AutoCompleteField from '../autocomplete-field'
import MultiSelectField from '../multi-select'
import SelectField from '../dropdown-field'
import CheckboxSingle from '../../book/steps/components/CheckboxSingle'
import CalendarField from '../calendar-field'
import { mq } from '../../../helpers'
import { useAllMarkets } from '../../../services'
import { useScrollToFirstError } from '../utils'

const EventForm = ({ fieldRefs }) => {
  const form = useForm()

  const markets = useAllMarkets()
  const [showMarketDropdown, setShowMarketDropdown] = useState(false)

  const handleLocationMarketToggle = () => {
    form.change('Event Location', '')
    setShowMarketDropdown((s) => !s)
  }

  useScrollToFirstError(form, fieldRefs)

  return (
    <EventFormWrapper>
      <div className="event-form-inner">
        <div className="detail-section">
          <h2>Contact Details</h2>

          <div className="content">
            <Field
              component={InputField}
              placeholder="First Name"
              name="First Name"
              ref={(el) => (fieldRefs.current['First Name'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Last Name"
              name="Last Name"
              ref={(el) => (fieldRefs.current['Last Name'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Phone Number"
              name="Phone Number"
              parse={(value) => value && value.replace(/[^\d+\-\s()]/g, '')}
              inputMode="tel"
              ref={(el) => (fieldRefs.current['Phone Number'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Email"
              name="Email"
              ref={(el) => (fieldRefs.current['Email'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Company (Optional)"
              name="Brand Affiliation"
            />
          </div>
        </div>

        <div className="event-detail-section">
          <h2>Event details</h2>
          <MultiSelectField
            name="Services"
            placeholder="Services"
            options={['Hair', 'Makeup', 'Nails']}
            ref={(el) => (fieldRefs.current['Services'] = el)}
            boldTitle={true}
          />
          <MultiSelectField
            name="Type of Services"
            placeholder="Type of Services"
            options={['Full Glam', 'Express/Touch-Up', 'Children']}
            ref={(el) => (fieldRefs.current['Type of Services'] = el)}
            boldTitle={true}
          />
          <h2>Address</h2>
          <div className="checkbox-container">
            <CheckboxSingle
              textComponent={() => (
                <Text
                  fontSize={18}
                  lineHeight="120%"
                  spacing="0.36px"
                  margin="0 0 0 20px"
                >
                  I have the address
                </Text>
              )}
              name="showMarket"
              value={!showMarketDropdown}
              whiteUnchecked={true}
              onChange={handleLocationMarketToggle}
              cobaltChecked={true}
            />
            <CheckboxSingle
              textComponent={() => (
                <Text
                  fontSize={18}
                  lineHeight="120%"
                  spacing="0.36px"
                  margin="0 0 0 20px"
                >
                  I don&apos;t know the address yet
                </Text>
              )}
              name="showMarket"
              value={showMarketDropdown}
              whiteUnchecked={true}
              onChange={handleLocationMarketToggle}
              cobaltChecked={true}
            />
          </div>
          <>
            {!showMarketDropdown && (
              <AutoCompleteField
                name="Event Location"
                placeholder="Address of the event"
                ref={(el) => (fieldRefs.current['Event Location'] = el)}
              />
            )}
            {!!markets && showMarketDropdown && markets.length && (
              <SelectField
                name="Event Location"
                placeholder="Location"
                options={markets.map((m) => m.name)}
                ref={(el) => (fieldRefs.current['Event Location'] = el)}
              />
            )}
          </>
          <div className="people-and-date-section">
            <SelectField
              name="Number of People"
              placeholder="Number of People Needing Services"
              options={[
                { value: '1', name: '1 Person' },
                { value: '2-4', name: '2-4 People' },
                { value: '5-9', name: '5-9 People' },
                { value: '10+', name: '10+ People' },
              ]}
              ref={(el) => (fieldRefs.current['Number of People'] = el)}
            />
            <CalendarField
              name="Event Date"
              placeholder="Event Date"
              ref={(el) => (fieldRefs.current['Event Date'] = el)}
              // * will keep calendar open for user to select 2 dates as a range
              // saved as [startDate, endDate]
              // isRange={true}
              format={false}
            />
          </div>
        </div>

        <div className="booking-notes-section">
          <h2>
            Budget <span className="optional-tag">(Optional)</span>
          </h2>
          <Field
            name="Budget"
            placeholder="If you have a budget range in mind, please share it."
            component={InputField}
          />
        </div>

        <div className="booking-notes-section">
          <h2>
            Booking Notes <span className="optional-tag">(Optional)</span>
          </h2>
          <Field
            name="Booking Notes"
            placeholder="Please add any additional event details."
            component={TextAreaField}
          />
        </div>

        <div className="attribution-section">
          <h2>
            How did you hear about us?
          </h2>
          <SelectField
            name="Attribution"
            placeholder="Select"
            options={[
              'Event',
              'Existing Client',
              'Facebook / Instagram',
              'Friend / Referral',
              'Google',
              'Influencer',
              'Press/News',
              'LinkedIn',
              'TikTok',
              `I don't recall`,
            ]}
          />
        </div>
      </div>
    </EventFormWrapper>
  )
}

export default EventForm

const EventFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;

  ${mq('max', '768px')} {
    width: 100%;
  }

  h2 {
    font-size: 20px;
  }

  .event-form-inner {
    max-width: 800px;
    width: 100%;
    margin: 80px 0 50px;
    display: flex;
    flex-direction: column;
    gap: 60px;

    ${mq('max', '800px')} {
      padding: 0 16px;
      margin: 40px 0 50px;
    }

    .detail-section {
      h2 {
        margin-bottom: 24px;
      }

      .content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;

        ${mq('max', '768px')} {
          grid-template-columns: 1fr;
        }

        div {
          margin: 0;
        }
      }
    }

    .event-detail-section {
      h2 {
        margin-bottom: 24px;
      }

      .people-and-date-section {
        margin-top: 24px;
        display: flex;
        justify-content: space-evenly;
        gap: 32px;

        ${mq('max', '768px')} {
          flex-direction: column;
          padding-bottom: 24px;
        }

        .calendar-button {
          height: 55px;
        }
      }

      .checkbox-container {
        margin: 24px 0;
        display: flex;
        gap: 24px;

        label {
          display: flex;
          padding-left: 0;

          p {
            ${mq('max', '768px')} {
              transform: translateY(4px);
              margin-left: 12px;
            }
          }
        }
      }
    }

    h2 {
      .optional-tag {
        font-weight: 400 !important;
        color: ${gsColors.meredithGrey};
        font-size: 16px;
      }
    }

    .booking-notes-section {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .attribution-section {
      h2 {
        margin-bottom: 24px;
      }
    }
  }
`
